import { ref, onMounted, onUnmounted, nextTick } from 'vue'

/**
 * Composable para gestionar la accesibilidad y navegación por teclado
 */
export function useAccessibility() {
  const trapRef = ref<HTMLElement | null>(null)
  const previouslyFocusedElement = ref<HTMLElement | null>(null)

  /**
   * Elementos focusables estándar
   */
  const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]:not([contenteditable="false"])'
    ].join(',')

    return Array.from(container.querySelectorAll(focusableSelectors))
      .filter(element => {
        const el = element as HTMLElement
        return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
      }) as HTMLElement[]
  }

  /**
   * Trap focus dentro de un elemento específico
   */
  const trapFocus = (element: HTMLElement) => {
    trapRef.value = element
    previouslyFocusedElement.value = document.activeElement as HTMLElement

    const focusableElements = getFocusableElements(element)
    if (focusableElements.length === 0) return

    // Focus en el primer elemento
    focusableElements[0].focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusableElements = getFocusableElements(element)
      const firstFocusable = focusableElements[0]
      const lastFocusable = focusableElements[focusableElements.length - 1]

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          event.preventDefault()
          lastFocusable.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          event.preventDefault()
          firstFocusable.focus()
        }
      }
    }

    element.addEventListener('keydown', handleKeyDown)
    
    return () => {
      element.removeEventListener('keydown', handleKeyDown)
    }
  }

  /**
   * Restaurar el focus al elemento anterior
   */
  const restoreFocus = () => {
    if (previouslyFocusedElement.value) {
      previouslyFocusedElement.value.focus()
      previouslyFocusedElement.value = null
    }
  }

  /**
   * Skip links para navegación
   */
  const createSkipLink = (targetId: string, text: string) => {
    const skipLink = document.createElement('a')
    skipLink.href = `#${targetId}`
    skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-blue-600 text-white px-4 py-2 rounded z-50'
    skipLink.textContent = text
    
    skipLink.addEventListener('click', (e) => {
      e.preventDefault()
      const target = document.getElementById(targetId)
      if (target) {
        target.focus()
        target.scrollIntoView({ behavior: 'smooth' })
      }
    })

    return skipLink
  }

  /**
   * Gestión de announcements para lectores de pantalla
   */
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcement = document.createElement('div')
    announcement.setAttribute('aria-live', priority)
    announcement.setAttribute('aria-atomic', 'true')
    announcement.className = 'sr-only'
    
    document.body.appendChild(announcement)
    
    // Pequeño delay para asegurar que el lector lo detecte
    setTimeout(() => {
      announcement.textContent = message
    }, 100)

    // Limpiar después de 3 segundos
    setTimeout(() => {
      if (announcement.parentNode) {
        announcement.parentNode.removeChild(announcement)
      }
    }, 3000)
  }

  /**
   * Verificar si un elemento está visible
   */
  const isElementVisible = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect()
    const viewport = {
      width: window.innerWidth || document.documentElement.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight
    }

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= viewport.height &&
      rect.right <= viewport.width
    )
  }

  /**
   * Scroll suave a un elemento
   */
  const scrollToElement = async (
    elementOrSelector: HTMLElement | string,
    options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' }
  ) => {
    await nextTick()
    
    const element = typeof elementOrSelector === 'string'
      ? document.querySelector(elementOrSelector) as HTMLElement
      : elementOrSelector

    if (element) {
      element.scrollIntoView(options)
    }
  }

  /**
   * Gestión de escape key global
   */
  const handleEscapeKey = (callback: () => void) => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        callback()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }

  return {
    // Focus management
    trapFocus,
    restoreFocus,
    getFocusableElements,
    
    // Navigation
    createSkipLink,
    scrollToElement,
    
    // Screen reader
    announce,
    
    // Utilities
    isElementVisible,
    handleEscapeKey
  }
}

/**
 * Composable específico para manejo de modales accesibles
 */
export function useModalAccessibility() {
  const { trapFocus, restoreFocus, announce, handleEscapeKey } = useAccessibility()
  
  let cleanupFunctions: (() => void)[] = []

  const openModal = (modalElement: HTMLElement, options?: {
    title?: string
    closeCallback?: () => void
  }) => {
    // Anunciar apertura del modal
    if (options?.title) {
      announce(`Modal abierto: ${options.title}`)
    }

    // Trap focus
    const cleanupTrap = trapFocus(modalElement)
    if (cleanupTrap) cleanupFunctions.push(cleanupTrap)

    // Handle escape key
    if (options?.closeCallback) {
      const cleanupEscape = handleEscapeKey(options.closeCallback)
      cleanupFunctions.push(cleanupEscape)
    }

    // Prevenir scroll del body
    document.body.style.overflow = 'hidden'
  }

  const closeModal = (options?: { title?: string }) => {
    // Cleanup all event listeners
    cleanupFunctions.forEach(cleanup => cleanup())
    cleanupFunctions = []

    // Restaurar focus
    restoreFocus()

    // Restaurar scroll del body
    document.body.style.overflow = ''

    // Anunciar cierre del modal
    if (options?.title) {
      announce(`Modal cerrado: ${options.title}`)
    }
  }

  return {
    openModal,
    closeModal
  }
}